import {
  type CardLink,
  type CardLinkInput,
  platformOf,
  RAW_CONDITIONS,
  type SellerType,
} from "@pokifolio/contracts";
import { type SubmitEvent, useId, useState } from "react";

import { Button } from "../../components/ui/button";
import { Field } from "../../components/ui/field";
import { Input, Select, Textarea } from "../../components/ui/input";
import { describeError, serverFieldError } from "../../lib/errors";
import { RAW_CONDITION_LABELS } from "../research/format";

const CURRENCIES = ["USD", "EUR", "GBP", "NOK", "SEK", "DKK", "CAD", "AUD", "JPY"];
const DAMAGE = [
  ["scratches", "Scratches"],
  ["whitening", "Whitening"],
  ["bends", "Bends or creases"],
  ["surfaceDamage", "Surface damage"],
] as const;

interface Values {
  physicalStore: boolean;
  url: string;
  storeName: string;
  storeAddress: string;
  sellerType: SellerType;
  openToBids: boolean;
  price: string;
  pricePaid: string;
  currency: string;
  condition: NonNullable<CardLinkInput["condition"]>;
  itemState: NonNullable<CardLinkInput["itemState"]>;
  scratches: boolean;
  whitening: boolean;
  bends: boolean;
  surfaceDamage: boolean;
  notes: string;
}

const empty = (keep?: Partial<Values>): Values => ({
  physicalStore: keep?.physicalStore ?? false,
  url: "",
  storeName: "",
  storeAddress: "",
  sellerType: keep?.sellerType ?? "shop",
  openToBids: false,
  price: "",
  pricePaid: "",
  currency: keep?.currency ?? "USD",
  condition: keep?.condition ?? "near_mint",
  itemState: keep?.itemState ?? "used",
  scratches: false,
  whitening: false,
  bends: false,
  surfaceDamage: false,
  notes: "",
});

const fromLink = (link: CardLink): Values => ({
  physicalStore: link.physicalStore,
  url: link.url ?? "",
  storeName: link.storeName ?? "",
  storeAddress: link.storeAddress ?? "",
  sellerType: link.sellerType,
  openToBids: link.openToBids,
  price: link.price ?? "",
  pricePaid: link.pricePaid ?? "",
  currency: link.currency,
  condition: link.condition,
  itemState: link.itemState,
  scratches: link.scratches,
  whitening: link.whitening,
  bends: link.bends,
  surfaceDamage: link.surfaceDamage,
  notes: link.notes ?? "",
});

interface LinkFormProps {
  /** Editing this listing; adding a new one when not given. */
  readonly link?: CardLink;
  readonly onSave: (values: CardLinkInput) => Promise<unknown>;
  readonly onCancel?: () => void;
}

/**
 * One listing's details. After adding, the form empties for the next one but keeps the currency, condition
 * and new/used, so many listings of the same card go in quickly.
 */
export function LinkForm({ link, onSave, onCancel }: LinkFormProps) {
  const [values, setValues] = useState<Values>(link === undefined ? empty() : fromLink(link));
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const id = useId();
  const set = <K extends keyof Values>(key: K, value: Values[K]) =>
    setValues((v) => ({ ...v, [key]: value }));
  const fieldError = (path: string) => (error === null ? undefined : serverFieldError(error, path));

  async function save() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(values);
      // Ready for the next listing of the same card; currency, condition and new/used are kept.
      if (link === undefined) setValues(empty(values));
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    void save();
  };
  const general =
    error !== null &&
    !["url", "storeName", "storeAddress", "price", "pricePaid", "currency", "notes"].some(
      (p) => fieldError(p) !== undefined,
    );

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-4"
      aria-label={link === undefined ? "Add listing" : "Edit listing"}
    >
      <label className="flex items-center gap-2 text-sm font-medium text-foreground">
        <input
          type="checkbox"
          checked={values.physicalStore}
          onChange={(e) => set("physicalStore", e.target.checked)}
          className="size-4 accent-primary"
        />
        Physical store (no website link)
      </label>
      {values.physicalStore ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id={`${id}-store`} label="Store name" error={fieldError("storeName")}>
            <Input value={values.storeName} onChange={(e) => set("storeName", e.target.value)} />
          </Field>
          <Field id={`${id}-address`} label="Address (optional)" error={fieldError("storeAddress")}>
            <Input value={values.storeAddress} onChange={(e) => set("storeAddress", e.target.value)} />
          </Field>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Field id={`${id}-url`} label="Listing URL" error={fieldError("url")}>
              <Input
                value={values.url}
                onChange={(e) => set("url", e.target.value)}
                placeholder="https://www.ebay.com/itm/…"
                autoComplete="off"
              />
            </Field>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-foreground">Platform</span>
            <p className="flex h-9 items-center text-sm text-muted-foreground" aria-live="polite">
              {values.url.trim() === "" ? "Detected from the URL" : `${platformOf(values.url)} (detected)`}
            </p>
          </div>
          <Field id={`${id}-seller`} label="Seller">
            <Select
              value={values.sellerType}
              onChange={(e) => set("sellerType", e.target.value === "private" ? "private" : "shop")}
            >
              <option value="shop">Shop</option>
              <option value="private">Private seller</option>
            </Select>
          </Field>
        </div>
      )}
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          checked={values.openToBids}
          onChange={(e) => set("openToBids", e.target.checked)}
          className="size-4 accent-primary"
        />
        Open to bids (no fixed price)
      </label>
      <div className="grid gap-4 sm:grid-cols-4">
        {values.openToBids ? (
          <div className="flex flex-col justify-end pb-2 text-sm text-muted-foreground">No fixed price</div>
        ) : (
          <Field id={`${id}-price`} label="Price" error={fieldError("price")}>
            <Input value={values.price} onChange={(e) => set("price", e.target.value)} inputMode="decimal" />
          </Field>
        )}
        <Field id={`${id}-currency`} label="Currency" error={fieldError("currency")}>
          <Select value={values.currency} onChange={(e) => set("currency", e.target.value)}>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
        <Field id={`${id}-condition`} label="Condition">
          <Select
            value={values.condition}
            onChange={(e) => set("condition", e.target.value as Values["condition"])}
          >
            {RAW_CONDITIONS.map((c) => (
              <option key={c} value={c}>
                {RAW_CONDITION_LABELS[c]}
              </option>
            ))}
          </Select>
        </Field>
        <Field id={`${id}-state`} label="New or used">
          <Select
            value={values.itemState}
            onChange={(e) => set("itemState", e.target.value === "new" ? "new" : "used")}
          >
            <option value="used">Used</option>
            <option value="new">New</option>
          </Select>
        </Field>
      </div>
      <fieldset className="flex flex-wrap gap-x-6 gap-y-2">
        <legend className="mb-1 text-sm font-medium text-foreground">Visible damage</legend>
        {DAMAGE.map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={values[key]}
              onChange={(e) => set(key, e.target.checked)}
              className="size-4 accent-primary"
            />
            {label}
          </label>
        ))}
      </fieldset>
      <div className="sm:max-w-xs">
        <Field id={`${id}-paid`} label="Price paid (optional)" error={fieldError("pricePaid")}>
          <Input
            value={values.pricePaid}
            onChange={(e) => set("pricePaid", e.target.value)}
            inputMode="decimal"
          />
        </Field>
      </div>
      <Field id={`${id}-notes`} label="Notes (optional)" error={fieldError("notes")}>
        <Textarea value={values.notes} onChange={(e) => set("notes", e.target.value)} maxLength={2000} />
      </Field>
      {general && (
        <p role="alert" className="text-sm text-destructive">
          {describeError(error).message}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={busy}>
          {busy ? "Saving…" : link === undefined ? "Save listing" : "Save changes"}
        </Button>
        {onCancel !== undefined && (
          <Button variant="ghost" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
